import CurrencyDollarCircleIcon from "@redotech/redo-icons/currency-dollar-circle.svg";
import FlipBackwardIcon from "@redotech/redo-icons/flip-backward.svg";
import GiftIcon from "@redotech/redo-icons/gift-01.svg";
import MessageChatCircleIcon from "@redotech/redo-icons/message-chat-circle.svg";
import RefreshCwIcon from "@redotech/redo-icons/refresh-cw-01.svg";
import SettingsIcon from "@redotech/redo-icons/settings-02.svg";
import ShoppingCartIcon from "@redotech/redo-icons/shopping-cart-01.svg";
import TrendUpIcon from "@redotech/redo-icons/trend-up-01_filled.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { JSXElementConstructor, memo } from "react";
import { COMMERCE_AGENT_URL } from "../../external-urls/commerce-agent-url";
import { OnboardingProps } from "../common";
import commerceAgentConversationUrl from "./commerce-agent-launch-assets/conversation.png?url";
import recoverNonSubscriberTanHeroUrl from "./recover-product-announcement-assets/recover-non-subscribed-tan.png?url";
import recoverSubscriberTanHeroUrl from "./recover-product-announcement-assets/recover-subscribed-tan.png?url";
import * as styles from "./recover-product-announcement-modal.module.css";

type RecoverAnnouncementVariant =
  | "subscriber"
  | "non-subscriber"
  | "non-subscriber-offer-2500"
  | "non-subscriber-offer-5000"
  | "commerce-agent";

const RECOVER_MEETING_URL =
  "https://meetings.hubspot.com/colby57?uuid=d78b7410-24d4-492e-9ff3-3432f77c555d";

const NON_SUBSCRIBER_BODY =
  "Most abandoned checkouts never opted into marketing — so your flows never reach them. Recover wins them back with personalized 1:1 texts.";

const NON_SUBSCRIBER_BENEFITS: {
  Icon: JSXElementConstructor<{ className?: string }>;
  label: string;
}[] = [
  {
    Icon: MessageChatCircleIcon,
    label: "Reach abandoned checkouts your SMS can't touch",
  },
  {
    Icon: TrendUpIcon,
    label: "Brands recover 5–10% of otherwise lost revenue",
  },
  {
    Icon: CurrencyDollarCircleIcon,
    label: "$0/month — pay only on revenue we recover",
  },
];

function offerBenefits(amountLabel: string) {
  return [
    {
      Icon: GiftIcon,
      label: `Your first ${amountLabel} in attributed revenue is on us`,
    },
    {
      Icon: MessageChatCircleIcon,
      label: "Reach abandoned checkouts your SMS can't touch",
    },
    {
      Icon: CurrencyDollarCircleIcon,
      label: "$0/month — pay only on revenue we recover",
    },
  ];
}

const CONTENT: Record<
  RecoverAnnouncementVariant,
  {
    imageAlt: string;
    imageSrc: string;
    eyebrow?: string;
    title: string;
    titleClassName?: string;
    body: string;
    benefits: {
      Icon: JSXElementConstructor<{ className?: string }>;
      label: string;
    }[];
    primaryCta: string;
    secondaryCta?: string;
  }
> = {
  subscriber: {
    imageAlt: "Recover subscription replenishment conversation",
    imageSrc: recoverSubscriberTanHeroUrl,
    title: "Supercharge Recover",
    body: "Recover already wins back abandoned checkouts. Put the same 1:1 conversations to work across the rest of the customer journey.",
    benefits: [
      { Icon: RefreshCwIcon, label: "Turn one-time buyers into subscribers" },
      {
        Icon: FlipBackwardIcon,
        label: "Win back lapsed customers before they're gone",
      },
      {
        Icon: ShoppingCartIcon,
        label: "Send upsell and replenishment reminders",
      },
    ],
    secondaryCta: "Learn more",
    primaryCta: "Add touchpoints",
  },
  "non-subscriber": {
    imageAlt: "Recover abandoned cart conversation",
    imageSrc: recoverNonSubscriberTanHeroUrl,
    title: "Recover lost revenue",
    body: NON_SUBSCRIBER_BODY,
    benefits: NON_SUBSCRIBER_BENEFITS,
    primaryCta: "See more",
  },
  "non-subscriber-offer-2500": {
    imageAlt: "Recover abandoned cart conversation",
    imageSrc: recoverNonSubscriberTanHeroUrl,
    title: "Your first $2,500, on us",
    titleClassName: styles.titleOffer,
    body: NON_SUBSCRIBER_BODY,
    benefits: offerBenefits("$2,500"),
    primaryCta: "Claim offer",
  },
  "non-subscriber-offer-5000": {
    imageAlt: "Recover abandoned cart conversation",
    imageSrc: recoverNonSubscriberTanHeroUrl,
    title: "Your first $5,000, on us",
    titleClassName: styles.titleOffer,
    body: NON_SUBSCRIBER_BODY,
    benefits: offerBenefits("$5,000"),
    primaryCta: "Claim offer",
  },
  "commerce-agent": {
    imageAlt: "Commerce Agent texting a shopper through an exchange",
    imageSrc: commerceAgentConversationUrl,
    eyebrow: "Introducing Commerce Agent",
    title: "Give every shopper a real conversation with your brand",
    body: "Redo Commerce Agent starts the conversation, answers the question, finds the product, builds the cart, tracks the shipment, and processes the exchange. All without leaving the text thread. Available free for a limited time.",
    benefits: [
      {
        Icon: MessageChatCircleIcon,
        label: "Starts conversations at cart, browse, and post-purchase",
      },
      {
        Icon: FlipBackwardIcon,
        label: "Fixes tracking, exchanges, and edits in the thread",
      },
      {
        Icon: SettingsIcon,
        label: "You set the voice, the offers, and the off switches",
      },
    ],
    secondaryCta: "Dismiss",
    primaryCta: "Learn more",
  },
};

function RecoverProductAnnouncementModal({
  handleDismiss,
  handleComplete,
  variant,
  onPrimary,
  onSecondary,
  primaryCta,
}: OnboardingProps & {
  variant: RecoverAnnouncementVariant;
  onPrimary?: () => void;
  onSecondary?: () => void;
  primaryCta?: string;
}) {
  const content = CONTENT[variant];

  const openMeetingLink = async () => {
    window.open(RECOVER_MEETING_URL, "_blank", "noopener,noreferrer");
    await handleComplete();
  };
  const runPrimary = onPrimary ?? (() => void openMeetingLink());
  const runSecondary = onSecondary ?? (() => void openMeetingLink());

  return (
    <RedoBaseModal
      darkMode
      isOpen
      onModalCloseRequested={handleDismiss}
      otherClasses={[styles.modal]}
    >
      <div className={styles.shell}>
        {/* design-allow: circular overlay close on the hero, same control as the giving-day announcement */}
        <button
          aria-label="Close"
          className={styles.closeButton}
          onClick={handleDismiss}
          type="button"
        >
          <XCloseIcon />
        </button>
        <div className={styles.content}>
          {
            // announcement eyebrow keeps this shell's type //redontIgnore
            <p className={styles.eyebrow}>
              {content.eyebrow ?? "Redo Recover"}
            </p>
          }
          <header className={styles.heading}>
            <h2
              className={
                content.titleClassName
                  ? `${styles.title} ${content.titleClassName}`
                  : styles.title
              }
            >
              {content.title}
            </h2>
            <p className={styles.bodyText}>{content.body}</p>
          </header>

          <div className={styles.benefits}>
            {content.benefits.map(({ Icon, label }) => (
              <div className={styles.benefitCard} key={label}>
                <span className={styles.benefitIcon}>
                  <Icon />
                </span>
                <span className={styles.benefitLabel}>{label}</span>
              </div>
            ))}
          </div>

          <div className={styles.actions}>
            {content.secondaryCta ? (
              <RedoButton
                hierarchy="secondary"
                onClick={runSecondary}
                size="sm"
                text={content.secondaryCta}
              />
            ) : null}
            <RedoButton
              hierarchy="primary"
              onClick={runPrimary}
              size="sm"
              text={primaryCta ?? content.primaryCta}
            />
          </div>
        </div>
        <div className={styles.imagePanel}>
          <img
            alt={content.imageAlt}
            className={styles.image}
            src={content.imageSrc}
          />
        </div>
      </div>
    </RedoBaseModal>
  );
}

export const RecoverSubscriberAnnouncementModal = memo(
  function RecoverSubscriberAnnouncementModal(props: OnboardingProps) {
    return <RecoverProductAnnouncementModal {...props} variant="subscriber" />;
  },
);

export const RecoverNonSubscriberAnnouncementModal = memo(
  function RecoverNonSubscriberAnnouncementModal(props: OnboardingProps) {
    return (
      <RecoverProductAnnouncementModal {...props} variant="non-subscriber" />
    );
  },
);

export const RecoverNonSubscriberOffer2500Modal = memo(
  function RecoverNonSubscriberOffer2500Modal(props: OnboardingProps) {
    return (
      <RecoverProductAnnouncementModal
        {...props}
        variant="non-subscriber-offer-2500"
      />
    );
  },
);

export const CommerceAgentLaunchModal = memo(function CommerceAgentLaunchModal(
  props: OnboardingProps,
) {
  return (
    <RecoverProductAnnouncementModal
      {...props}
      onPrimary={() => {
        window.open(COMMERCE_AGENT_URL, "_blank", "noopener,noreferrer");
        void props.handleComplete();
      }}
      onSecondary={() => {
        void props.handleDismiss();
      }}
      variant="commerce-agent"
    />
  );
});

export const RecoverNonSubscriberOffer5000Modal = memo(
  function RecoverNonSubscriberOffer5000Modal(props: OnboardingProps) {
    return (
      <RecoverProductAnnouncementModal
        {...props}
        variant="non-subscriber-offer-5000"
      />
    );
  },
);
